<template>
  <div>
    <!-- Filters -->
    <div v-if="showAnimalsList">
      <div class="field">
        <label class="label">Animal Type</label>
        <div class="control">
          <label class="radio">
            <input type="radio" value="" v-model="categoryFilter" />
            All
          </label>
          <label class="radio">
            <input type="radio" value="mammal" v-model="categoryFilter" />
            Mammals
          </label>
          <label class="radio">
            <input type="radio" value="bird" v-model="categoryFilter" />
            Birds
          </label>
        </div>
      </div>

      <div class="field" v-if="filterableCountryCodes">
        <label class="label">Country</label>
        <div class="control">
          <div class="select">
            <select v-model="filterByCountryCode">
              <option value="">All</option>
              <option
                v-for="countryCode in filterableCountryCodes"
                :key="countryCode"
                :value="countryCode"
                >{{ countryCode }}</option
              >
            </select>
          </div>
        </div>
      </div>

      <label class="label" v-if="filterByCountryCode && filterByCountryCode !== ''">Date</label>
      <div class="field has-addons" v-if="filterByCountryCode && filterByCountryCode !== ''">
        <div class="control is-expanded">
          <FlatPickr
            v-model="filterByDate"
            :config="datePickerConfig"
            class="input"
            placeholder="Any"
          />
        </div>

        <div class="control" v-if="filterByDate && filterByDate !== ''">
          <a class="button inline-block" @click="clearDateFilter">
            <span class="icon is-small">
              <i class="fas icon-x"></i>
            </span>
          </a>
        </div>
      </div>

      <div class="field" v-if="filterableDistricts">
        <label class="label">District</label>
        <div class="control">
          <div class="select">
            <select v-model="filterByDistrictId">
              <option value="">All</option>
              <option
                v-for="district in filterableDistricts"
                :key="district.id"
                :value="district.id"
                >{{ district.name }}</option
              >
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">Name</label>
        <div class="control has-icons-left">
          <input class="input" type="text" placeholder="Filter name" v-model="filterInput" />
          <span class="icon is-small is-left">
            <i class="icon-search"></i>
          </span>
        </div>
      </div>
    </div>

    <div class="add-animal">
      <a
        class="button is-primary"
        :disabled="isAddingAnimal"
        @click="addAnimal"
        :class="{ 'is-loading': isAddingAnimal }"
      >
        Add animal
      </a>
    </div>

    <!-- Add animal error -->
    <div class="notification is-danger" v-if="addAnimalError">
      {{ addAnimalError }}
    </div>

    <Spinner v-if="isLoading" />

    <div class="notification is-danger" v-if="loadError">{{ loadError }}</div>

    <!-- Animals list -->
    <div class="panel" v-if="showAnimalsList">
      <a
        class="panel-block animal-list-row"
        v-for="animal in filteredAnimals"
        :key="animal.animalId"
        @click="animalSelected(animal)"
        :class="{
          'has-background-grey-dark': isSelectedAnimal(animal),
          'has-text-light': isSelectedAnimal(animal)
        }"
      >
        <!-- <figure class="image is-32x32">
            <img :src="animal.iconURL" />
          </figure> -->

        <div class="container">
          <span class="is-pulled-left">{{
            animal.name ? animal.name : '[' + animal.animalId + ']'
          }}</span>
        </div>
      </a>
    </div>

    <div class="table-footer">
      <span v-if="showAnimalsList">{{ filteredAnimals.length }} animals</span>
    </div>
  </div>
</template>

<script>
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'

import Spinner from '@/components/utils/Spinner'

import {
  LOAD_ANIMALS,
  IS_LOADING,
  GET_LOAD_ERROR,
  GET_LOAD_RESULT,
  ADD_ANIMAL,
  SELECT_ANIMAL_WITH_ID,
  GET_SELECTED_ANIMAL
} from '@/modules/animals/animalsStore'

export default {
  name: 'AnimalsSearch',
  components: {
    Spinner,
    FlatPickr
  },

  props: {
    animalsStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      categoryFilter: '',
      filterByCountryCode: '',
      filterByDate: '',
      filterByDistrictId: '',
      filterInput: '',
      isAddingAnimal: false,
      addAnimalError: null
    }
  },

  mounted() {
    if (this.$route.name === 'animalsSearch') {
      const setSelectedAnimalIdAction = `${this.animalsStorePath}/${SELECT_ANIMAL_WITH_ID}`
      this.$store.dispatch(setSelectedAnimalIdAction, { animalId: null })
    }

    if (!this.animalData) {
      this.loadAnimals()
    }

    this.categoryFilter = this.$route.query.category || ''
    this.filterByCountryCode = this.$route.query.countryCode || ''
    this.filterByDate = this.$route.query.date || ''
    this.filterByDistrictId = this.$route.query.districtId || ''
    this.filterInput = this.$route.query.filter || ''
  },

  methods: {
    loadAnimals() {
      const loadAnimalsAction = `${this.animalsStorePath}/${LOAD_ANIMALS}`
      this.$store.dispatch(loadAnimalsAction)
    },

    isSelectedAnimal(animal) {
      const getSelectedAnimal = `${this.animalsStorePath}/${GET_SELECTED_ANIMAL}`
      const selectedAnimal = this.$store.getters[getSelectedAnimal]

      if (!selectedAnimal) {
        return false
      }

      return selectedAnimal.animalId === animal.animalId
    },

    clearDateFilter() {
      this.filterByDate = ''
    },

    animalSelected(animal) {
      this.showAnimalDetails(animal.animalId)
    },

    async addAnimal() {
      this.isAddingAnimal = true
      this.addAnimalError = null

      const animal = {
        category: this.categoryFilter !== '' ? this.categoryFilter : 'mammal',
        name: '[new animal]'
      }

      try {
        const addAnimalAction = `${this.animalsStorePath}/${ADD_ANIMAL}`
        const response = await this.$store.dispatch(addAnimalAction, { animal })

        const animalId = response.data.animalId
        this.showAnimalDetails(animalId)

        this.loadAnimals()
      } catch (error) {
        this.addAnimalError = error || 'Something went wrong.'
      } finally {
        this.isAddingAnimal = false
      }
    },

    showAnimalDetails(animalId) {
      this.$router
        .push({
          name: 'animalDetails',
          query: {
            category: this.categoryFilter,
            countryCode: this.filterByCountryCode,
            date: this.filterByDate,
            districtId: this.filterByDistrictId,
            filter: this.filterInput
          },
          params: { animalId }
        })
        .catch(() => {}) // Will throw exception if navigating to the same route, this is ok so swallow error
    },

    updateRouteQuery() {
      if (!this.$route.params.animalId) return

      this.showAnimalDetails(this.$route.params.animalId)
    }
  },

  computed: {
    datePickerConfig() {
      return {
        weekNumbers: true,
        dateFormat: 'Y-m-d',
        locale: {
          firstDayOfWeek: 1
        }
      }
    },

    isLoading() {
      const isLoading = `${this.animalsStorePath}/${IS_LOADING}`
      return this.$store.getters[isLoading]
    },

    showAnimalsList() {
      if (!this.animals) {
        return false
      }

      return this.animals.length > 0
    },

    animalData() {
      const getAnimalData = `${this.animalsStorePath}/${GET_LOAD_RESULT}`
      return this.$store.getters[getAnimalData]
    },

    animals() {
      if (!this.animalData) {
        return null
      }

      return this.animalData.animals
    },

    filterableCountryCodes() {
      if (!this.animalData) {
        return null
      }

      if (!this.animalData.allCountryCodes) {
        return null
      }

      var sortedCountryCodes = [...this.animalData.allCountryCodes]
      sortedCountryCodes.sort()

      return sortedCountryCodes
    },

    filterableDistricts() {
      if (!this.animalData) {
        return null
      }

      if (!this.animalData.allDistricts) {
        return null
      }

      if (!this.filterByCountryCode || this.filterByCountryCode === '') {
        return null
      }

      if (!this.filterByDate || this.filterByDate === '') {
        return null
      }

      var filteredDistricts = this.animalData.allDistricts.filter(
        district => district.countryCode === this.filterByCountryCode
      )
      filteredDistricts.sort((district1, district2) =>
        district1.name.localeCompare(district2.name, 'sv')
      )

      return filteredDistricts
    },

    filteredAnimals() {
      if (!this.animals) {
        return null
      }

      var filteredAnimals = [...this.animals]
      filteredAnimals.sort((animal1, animal2) => {
        if (!animal1.name && !animal2.name) {
          return animal1.animalId.localeCompare(animal2.animalId)
        }

        if (animal1.name && !animal2.name) {
          return -1
        }

        if (!animal1.name && animal2.name) {
          return 1
        }

        return animal1.name.localeCompare(animal2.name)
      })

      if (this.categoryFilter && this.categoryFilter !== '') {
        filteredAnimals = filteredAnimals.filter(animal => animal.category === this.categoryFilter)
      }

      if (this.filterByCountryCode && this.filterByCountryCode !== '') {
        filteredAnimals = filteredAnimals.filter(
          animal => animal.countryCodes && animal.countryCodes.includes(this.filterByCountryCode)
        )

        if (this.filterByDate && this.filterByDate !== '') {
          filteredAnimals = filteredAnimals.filter(animal => {
            if (!animal.periods) {
              return false
            }

            if (
              animal.periods.find(period => {
                if (period.countryCode !== this.filterByCountryCode) return false
                if (period.from > this.filterByDate) return false
                if (period.to < this.filterByDate) return false

                if (this.filterByDistrictId && this.filterByDistrictId !== '') {
                  if (!period.districtIds || !period.districtIds.includes(this.filterByDistrictId))
                    return false
                }

                return true
              })
            ) {
              return true
            }

            return false
          })
        }
      }

      if (this.filterInput && this.filterInput !== '') {
        const timmedFilterInput = this.filterInput.trim().toUpperCase()

        filteredAnimals = filteredAnimals.filter(animal => {
          if (animal.name && animal.name.toUpperCase().indexOf(timmedFilterInput) !== -1) {
            return true
          }

          if (animal.localizations) {
            for (let index = 0; index < animal.localizations.length; index++) {
              const localization = animal.localizations[index]
              if (localization.name.toUpperCase().indexOf(timmedFilterInput) !== -1) {
                return true
              }
            }
          }

          return false
        })
      }

      return filteredAnimals
    },

    loadError() {
      const getLoadError = `${this.animalsStorePath}/${GET_LOAD_ERROR}`
      return this.$store.getters[getLoadError]
    }
  },

  watch: {
    categoryFilter() {
      this.updateRouteQuery()
    },

    filterByCountryCode() {
      this.updateRouteQuery()

      if (!this.filterableDistricts) {
        return
      }

      if (!this.filterByDistrictId || this.filterByDistrictId === '') {
        return
      }

      if (!this.filterableDistricts.find(district => district.id === this.filterByDistrictId)) {
        this.filterByDistrictId = ''
      }
    },

    filterByDate() {
      if (!this.filterByDate || this.filterByDate === '') {
        this.filterByDistrictId = ''
      }

      this.updateRouteQuery()
    },

    filterByDistrictId() {
      this.updateRouteQuery()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/style.scss';

.select {
  width: 100%;
}

select {
  width: 100%;
}

form {
  padding-bottom: $column-gap;
}

.animal-list-row {
  min-height: 41px;
}

.panel {
  margin-bottom: 2px !important;
}

.add-animal {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 1em;
  margin-bottom: 1em;
}

.table-footer {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  span {
    color: $grey;
    font-size: 0.9rem;
  }
}
</style>
